import React, { useEffect, useRef, useState } from 'react';
import {
  getViewportIcon,
  displayViewportName,
  isSingleLinkMode,
  rangeLabel,
  availableBreakpoints,
  addBreakpoint,
  getInheritingFrom,
  validateViewports,
  isWrongUrlType,
} from '../../utils/viewportUtils';
import { figmaFileName } from '../../utils/figmaPaste';
import useFigmaLinkRows from '../../utils/useFigmaLinkRows';
import FigmaLinkNote from './FigmaLinkNote';
import ShareGuideModal from './ShareGuideModal';
import AeroFigmaLinkNote from '../Aero/AeroFigmaLinkNote';
import AeroShareGuideModal from '../Aero/AeroShareGuideModal';
import './ViewportEditor.css';

/**
 * Reusable viewport editor: the per-breakpoint Figma link rows plus inline
 * validation, lifted verbatim from FormContent so Figmafolio and Aero share
 * one implementation. The component boundary is intentionally generic:
 *
 *   - viewports          the array of { name, minWidth, figmaUrl }
 *   - onViewportsChange   called with the next array on every edit
 *   - variant             'default' (Figmafolio) | 'aero' (re-skinned)
 *   - linkCheck           Figmafolio only. Turns design / file / branch links into
 *                         prototype links on paste, asks the server whether anyone can
 *                         open the prototype (one site at a time, from this page only),
 *                         and shows the result under the field. Never blocks saving.
 *
 * It owns no save/preview action: those are caller concerns. Editing rules are
 * unchanged: 2 to 6 viewports, a locked 0px base row, editable name and width,
 * empty links inherit from the next larger viewport (the row says "Same as Desktop"), and a row
 * with its own link has Reset. Rows run biggest first. A new site starts as one row, "All screens";
 * "Add breakpoint" opens a short menu (Phone, Tablet, Large desktop, custom width).
 */
export default function ViewportEditor({ viewports = [], onViewportsChange, variant = 'default', linkCheck = false }) {
  const [editingName, setEditingName] = useState(null);
  const [editingWidth, setEditingWidth] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [phoneShown, setPhoneShown] = useState(false); // left the one-row start
  const [menuOpen, setMenuOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const menuRef = useRef(null);

  // The one-row start: the two default rows, no phone link. "All screens" is the Desktop row; an
  // empty Phone row borrows it, so one link serves every screen size.
  const single = !phoneShown && isSingleLinkMode(viewports);

  // Biggest first, like Webflow and Framer: the row the others borrow from is on top.
  const sortedViewports = [...viewports].sort((a, b) => b.minWidth - a.minWidth);
  const shownViewports = single ? sortedViewports.filter((v) => v.minWidth === 768) : sortedViewports;

  // The Aero variant uses the aero-input field styling (loaded with the Aero
  // theme); Figmafolio keeps its form-input class. Everything else is shared.
  const urlInputBase = variant === 'aero' ? 'aero-input' : 'form-input';

  // ---- link check (both brands): one hook, see utils/useFigmaLinkRows.js ----
  const setRowUrl = (minWidth, newUrl) => {
    onViewportsChange(viewports.map((v) => (v.minWidth === minWidth ? { ...v, figmaUrl: newUrl || null } : v)));
  };
  const linkRows = useFigmaLinkRows({
    rows: viewports.map((v) => ({ id: v.minWidth, url: v.figmaUrl })),
    setUrl: setRowUrl,
    enabled: linkCheck,
  });
  const Note = variant === 'aero' ? AeroFigmaLinkNote : FigmaLinkNote;
  const Guide = variant === 'aero' ? AeroShareGuideModal : ShareGuideModal;

  // close the Add breakpoint menu on an outside click or Escape
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleViewportUrlChange = (minWidth, newUrl) => {
    const updated = viewports.map((v) =>
      v.minWidth === minWidth ? { ...v, figmaUrl: newUrl || null } : v
    );
    onViewportsChange(updated);
  };

  const handleViewportNameChange = (minWidth, newName) => {
    const updated = viewports.map((v) =>
      v.minWidth === minWidth ? { ...v, name: newName } : v
    );
    onViewportsChange(updated);
  };

  const handleViewportWidthChange = (oldMinWidth, newMinWidth) => {
    const parsedWidth = parseInt(newMinWidth, 10);
    if (isNaN(parsedWidth) || parsedWidth < 1 || parsedWidth > 3840) {
      setNotice('Enter a width between 1 and 3840');
      return;
    }
    if (parsedWidth === oldMinWidth) return;

    // A width can only be used once
    if (viewports.some((v) => v.minWidth !== oldMinWidth && v.minWidth === parsedWidth)) {
      setNotice(`A breakpoint already starts at ${parsedWidth}px`);
      return;
    }

    setNotice('');
    // The rows sort themselves by width, so a row made bigger than the next one simply moves.
    onViewportsChange(viewports.map((v) => (v.minWidth === oldMinWidth ? { ...v, minWidth: parsedWidth } : v)));
  };

  const handleChoose = (id, width) => {
    setMenuOpen(false);
    setCustomOpen(false);
    setNotice('');
    if (id === 'phone') {
      setPhoneShown(true);
      return;
    }
    const result = addBreakpoint(viewports, id, width);
    if (result.error) {
      setNotice(result.error);
      return;
    }
    setPhoneShown(true);
    onViewportsChange(result.viewports);
  };

  const handleDeleteViewport = (minWidth) => {
    if (viewports.length <= 2) return;
    if (minWidth === 0) return; // Can't delete base viewport

    const updated = viewports.filter((v) => v.minWidth !== minWidth);
    onViewportsChange(updated);
    setDeleteConfirmId(null);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setEditingName(null);
    }
  };

  const handleWidthKeyDown = (e, oldMinWidth) => {
    if (e.key === 'Enter') {
      handleViewportWidthChange(oldMinWidth, e.target.value);
      setEditingWidth(null);
    } else if (e.key === 'Escape') {
      setEditingWidth(null);
    }
  };

  const handleWidthBlur = (e, oldMinWidth) => {
    handleViewportWidthChange(oldMinWidth, e.target.value);
    setEditingWidth(null);
  };

  // Check for validation errors
  const validationErrors = validateViewports(viewports);
  const hasUrlError = !linkCheck && viewports.some((v) => isWrongUrlType(v.figmaUrl));

  const menuItems = [
    ...(single ? [{ id: 'phone', name: 'Phone', hint: 'up to 767px' }] : []),
    ...availableBreakpoints(viewports),
  ];

  return (
    <div className={`viewport-editor${variant === 'aero' ? ' viewport-editor-aero' : ''}`}>
      <div className="viewport-list">
        {shownViewports.map((viewport) => {
          const inheritingFrom = getInheritingFrom(viewport, viewports);
          const isBaseViewport = viewport.minWidth === 0;
          const hasOther = viewports.some((o) => o.minWidth !== viewport.minWidth && o.figmaUrl);
          const label = single ? 'All screens' : displayViewportName(viewport.name);

          return (
            <div key={viewport.minWidth} className="viewport-row">
              <div className="viewport-info">
                <span className="viewport-icon">{single ? '\u{1F5A5}\uFE0F' : getViewportIcon(viewport.minWidth)}</span>

                {single ? (
                  <span className="viewport-name-static">{label}</span>
                ) : editingName === viewport.minWidth ? (
                  <input
                    type="text"
                    className="viewport-name-input"
                    value={viewport.name}
                    onChange={(e) => handleViewportNameChange(viewport.minWidth, e.target.value)}
                    onKeyDown={(e) => handleNameKeyDown(e, viewport.minWidth)}
                    onBlur={() => setEditingName(null)}
                    autoFocus
                  />
                ) : (
                  <button
                    className="viewport-name-btn"
                    onClick={() => setEditingName(viewport.minWidth)}
                    type="button"
                  >
                    {label}
                  </button>
                )}

                {single ? (
                  <span className="viewport-width-static">Every screen size</span>
                ) : editingWidth === viewport.minWidth && !isBaseViewport ? (
                  <input
                    type="number"
                    className="viewport-width-input"
                    defaultValue={viewport.minWidth}
                    onKeyDown={(e) => handleWidthKeyDown(e, viewport.minWidth)}
                    onBlur={(e) => handleWidthBlur(e, viewport.minWidth)}
                    min="1"
                    max="3840"
                    autoFocus
                  />
                ) : (
                  <button
                    className={`viewport-width-btn ${isBaseViewport ? 'locked' : ''}`}
                    onClick={() => !isBaseViewport && setEditingWidth(viewport.minWidth)}
                    type="button"
                    disabled={isBaseViewport}
                    title={isBaseViewport ? 'The smallest breakpoint starts at 0px' : 'Click to edit the width'}
                  >
                    {rangeLabel(viewport, viewports)}
                    {!isBaseViewport && (
                      <svg className="viewport-width-pencil" width="1em" height="1em" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M11 2.5l2.5 2.5L5.5 13H3v-2.5L11 2.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
                      </svg>
                    )}
                  </button>
                )}
              </div>

              <div className="viewport-url-container">
                <input
                  className={`${urlInputBase} viewport-url-input ${!linkCheck && isWrongUrlType(viewport.figmaUrl) ? 'error' : ''}`}
                  type="text"
                  aria-label={`${label} Figma link`}
                  placeholder={!single && inheritingFrom ? `Same as ${displayViewportName(inheritingFrom)}` : 'https://www.figma.com/proto/...'}
                  value={viewport.figmaUrl || ''}
                  onChange={(e) => handleViewportUrlChange(viewport.minWidth, e.target.value)}
                  onPaste={linkCheck ? (e) => linkRows.handlePaste(viewport.minWidth, e) : undefined}
                  onBlur={linkCheck ? (e) => linkRows.handleBlur(viewport.minWidth, e.target.value) : undefined}
                />
                {linkCheck && (
                  <Note
                    kind={linkRows.noteKind(viewport.minWidth, viewport.figmaUrl)}
                    onShowHow={() => linkRows.guide.open(viewport.minWidth)}
                    onRecheck={() => linkRows.recheck(viewport.minWidth, viewport.figmaUrl)}
                  />
                )}
                {!linkCheck && isWrongUrlType(viewport.figmaUrl) && (
                  <p className="figma-url-warning viewport-url-warning">
                    ⚠️ Use a prototype link (figma.com/proto/...)
                  </p>
                )}
                {!single && viewport.figmaUrl && hasOther && (
                  <button type="button" className="viewport-reset-btn" onClick={() => setRowUrl(viewport.minWidth, null)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M9 14L4 9l5-5" />
                      <path d="M4 9h10a6 6 0 010 12h-3" />
                    </svg>
                    Reset
                  </button>
                )}
                {!single && isBaseViewport && !viewport.figmaUrl && (
                  <p className="viewport-row-help">Copy the link to your phone flow's starting frame in Figma.</p>
                )}
              </div>

              <div className="viewport-actions">
                {!single && viewports.length > 2 && !isBaseViewport && (
                  <>
                    {deleteConfirmId === viewport.minWidth ? (
                      <div className="viewport-delete-confirm">
                        <span>Delete?</span>
                        <button
                          className="viewport-delete-confirm-btn yes"
                          onClick={() => handleDeleteViewport(viewport.minWidth)}
                          type="button"
                        >
                          Yes
                        </button>
                        <button
                          className="viewport-delete-confirm-btn no"
                          onClick={() => setDeleteConfirmId(null)}
                          type="button"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        className="viewport-delete-btn"
                        onClick={() => setDeleteConfirmId(viewport.minWidth)}
                        type="button"
                        title="Delete breakpoint"
                        aria-label={`Delete ${label}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}

        {menuItems.length > 0 && (
          <div className="viewport-add" ref={menuRef}>
            <button
              className="viewport-add-btn"
              onClick={() => setMenuOpen((o) => !o)}
              type="button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <svg viewBox="0 0 16 16" fill="none" width="16" height="16">
                <path d="M8 1v14M1 8h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Add breakpoint
            </button>
            {menuOpen && (
              <div className="viewport-menu" role="menu">
                {menuItems.map((item) =>
                  item.id === 'custom' ? (
                    customOpen ? (
                      <form
                        key={item.id}
                        className="viewport-menu-custom"
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleChoose('custom', e.target.elements.width.value);
                        }}
                      >
                        <input name="width" type="number" min="1" max="3840" placeholder="Width in px" aria-label="Width in pixels" autoFocus />
                        <button type="submit">Add</button>
                      </form>
                    ) : (
                      <button key={item.id} type="button" role="menuitem" className="viewport-menu-item" onClick={() => setCustomOpen(true)}>
                        <span>{item.name}</span>
                      </button>
                    )
                  ) : (
                    <button key={item.id} type="button" role="menuitem" className="viewport-menu-item" onClick={() => handleChoose(item.id)}>
                      <span>{item.name}</span>
                      <span className="viewport-menu-hint">{item.hint}</span>
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {notice && <p className="viewport-validation-error" role="alert">{notice}</p>}

      {linkCheck && (
        <Guide
          show={linkRows.guide.openFor !== null}
          onClose={linkRows.guide.close}
          recheck={linkRows.guide.recheckQuiet}
          fileName={figmaFileName((viewports.find((v) => v.minWidth === linkRows.guide.openFor) || {}).figmaUrl)}
          onShared={() => {}}
        />
      )}

      {validationErrors.length > 0 && !hasUrlError && (
        <div className="viewport-validation-errors">
          {validationErrors.map((error, idx) => (
            <p key={idx} className="viewport-validation-error">⚠️ {error}</p>
          ))}
        </div>
      )}
    </div>
  );
}
